import type {
  BaseQueryApi,
  FetchBaseQueryError,
  QueryReturnValue,
} from "@reduxjs/toolkit/query/react";

import { APIS } from "@/app/app-config";
import type { AuthInfo } from "@/app/types";
import { RYTE_FEATURE_FLAGS } from "@/features/ryteTracker/config/ryteFeatureFlagKeys";
import type {
  GetYardGoalsRequest,
  GetYardGoalsResponse,
  GoalEnvelope,
} from "@/features/ryteTracker/types";
import { isAllowListedAuthInfo } from "@/utils/allowListUtils";
import rumService, { RUM_EVENT_TYPES } from "@/utils/awsRUM/awsRUM";

export interface GoalTimeRange {
  startEpochMs: number;
  endEpochMs: number;
}

interface GetYardGoalsTransportArgs {
  apiName: string;
  url: string;
  method: "POST";
  body: GetYardGoalsRequest;
  skipHttp500Retry?: boolean;
}

type MaybePromise<T> = T | PromiseLike<T>;

export type GetYardGoalsBaseQuery = (
  args: GetYardGoalsTransportArgs
) => MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError, object>>;

interface FanoutQueryState {
  authInfo?: AuthInfo;
  featureFlags?: Record<string, { enabled?: boolean } | undefined>;
  ryteData?: { goalReadEpoch?: number };
}

interface GoalCandidate {
  row: unknown;
  version: number | undefined;
}

interface GoalAccumulator {
  byId: Map<string, GoalCandidate>;
  malformedRows: unknown[];
  rawRowCount: number;
  duplicateCount: number;
}

interface QueryMetrics extends Record<string, unknown> {
  mode: "wide" | "fanout";
  selectedConcurrency: 0 | 6;
  rangeDurationMs: number;
  chunkCount: number;
  successfulChunkCount: number;
  rawRowCount: number;
  uniqueRowCount: number;
  duplicateCount: number;
  malformedRowCount: number;
  durationMs: number;
  maxChunkDurationMs: number;
  splitCount: number;
  maxSplitDepth: number;
  failedChunkOrdinal?: number;
  errorStatus?: string | number;
  terminationReason: "success" | "dependency_failure" | "malformed" | "superseded" | "aborted";
}

type TerminationReason = QueryMetrics["terminationReason"];

interface MetricContext {
  mode: QueryMetrics["mode"];
  selectedConcurrency: QueryMetrics["selectedConcurrency"];
  rangeDurationMs: number;
  chunkCount: number;
  startedAt: number;
  accumulator?: GoalAccumulator;
  successfulChunkCount: number;
  maxChunkDurationMs: number;
  splitCount: number;
  maxSplitDepth: number;
}

interface CompletionDetails {
  terminationReason: TerminationReason;
  error?: FetchBaseQueryError;
  failedChunkOrdinal?: number;
  rawRowCount?: number;
  uniqueRowCount?: number;
  duplicateCount?: number;
  malformedRowCount?: number;
}

const FANOUT_UNEXPECTED_ERROR = "GetYardGoals fan-out failed unexpectedly";
const FANOUT_MALFORMED_ERROR = "GetYardGoals response malformed";
const FANOUT_ABORTED_ERROR = "GetYardGoals query aborted";
const FANOUT_SPLIT_LIMIT_ERROR = "GetYardGoals adaptive split limit reached";
export const GET_YARD_GOALS_SUPERSEDED_ERROR = "GetYardGoals query superseded";
const MAX_SPLIT_DEPTH = 5;
const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
const MAX_ADAPTIVE_TRANSPORT_CALLS = 14;
const INITIAL_CONCURRENCY = 6;

const customError = (error: string): FetchBaseQueryError => ({
  status: "CUSTOM_ERROR",
  error,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const readState = (api: Pick<BaseQueryApi, "getState">): FanoutQueryState =>
  api.getState() as FanoutQueryState;

const readGoalReadEpoch = (api: Pick<BaseQueryApi, "getState">): number =>
  readState(api).ryteData?.goalReadEpoch ?? 0;

const isFanoutEnabled = (api: Pick<BaseQueryApi, "getState">): boolean => {
  const state = readState(api);
  return (
    isAllowListedAuthInfo(state.authInfo) ||
    state.featureFlags?.[RYTE_FEATURE_FLAGS.ENABLE_RYTE_GOAL_QUERY_FANOUT]?.enabled === true
  );
};

const buildTransportArgs = (
  input: GetYardGoalsRequest,
  range: GoalTimeRange,
  skipHttp500Retry = false
): GetYardGoalsTransportArgs => ({
  apiName: APIS.GetYardGoals.name,
  url: APIS.GetYardGoals.resource,
  method: "POST",
  body: { yardId: input.yardId, ...range },
  ...(skipHttp500Retry ? { skipHttp500Retry: true } : {}),
});

const isValidEpochMs = (value: number): boolean =>
  Number.isSafeInteger(value) && Number.isFinite(new Date(value).getTime());

export const buildFiveHourGoalRanges = (
  startEpochMs: number,
  endEpochMs: number
): GoalTimeRange[] => {
  if (!isValidEpochMs(startEpochMs) || !isValidEpochMs(endEpochMs) || startEpochMs > endEpochMs) {
    return [];
  }

  const ranges: GoalTimeRange[] = [];
  let cursor = startEpochMs;
  while (cursor <= endEpochMs) {
    const remainingDurationMs = endEpochMs - cursor;
    const rangeEnd = remainingDurationMs <= FIVE_HOURS_MS ? endEpochMs : cursor + FIVE_HOURS_MS - 1;
    ranges.push({ startEpochMs: cursor, endEpochMs: rangeEnd });
    if (rangeEnd === endEpochMs) break;
    cursor = rangeEnd + 1;
  }
  return ranges;
};

const readGoalIdentity = (
  row: unknown
): { id: string | undefined; version: number | undefined } => {
  if (!isRecord(row)) return { id: undefined, version: undefined };
  const data = row.data;
  if (!isRecord(data)) return { id: undefined, version: undefined };
  const goal = data.goal;
  if (!isRecord(goal)) return { id: undefined, version: undefined };
  const goalId = goal.goalId;
  const rawId = isRecord(goalId) ? goalId.id : undefined;
  const id = typeof rawId === "string" && rawId.trim().length > 0 ? rawId : undefined;
  const rawVersion = goal.version;
  const version =
    typeof rawVersion === "number" && Number.isSafeInteger(rawVersion) && rawVersion > 0
      ? rawVersion
      : undefined;
  return { id, version };
};

const createAccumulator = (): GoalAccumulator => ({
  byId: new Map<string, GoalCandidate>(),
  malformedRows: [],
  rawRowCount: 0,
  duplicateCount: 0,
});

const accumulateResponse = (accumulator: GoalAccumulator, data: unknown): boolean => {
  if (!isRecord(data) || !Array.isArray(data.goals)) return false;
  if (data.goals.some((row) => row == null)) return false;

  for (const row of data.goals) {
    accumulator.rawRowCount += 1;
    const { id, version } = readGoalIdentity(row);
    if (!id) {
      accumulator.malformedRows.push(row);
      continue;
    }

    const existing = accumulator.byId.get(id);
    if (!existing) {
      accumulator.byId.set(id, { row, version });
      continue;
    }

    accumulator.duplicateCount += 1;
    const incomingWins =
      version !== undefined && (existing.version === undefined || version > existing.version);
    if (incomingWins) {
      accumulator.byId.set(id, { row, version });
    }
  }
  return true;
};

const accumulatedResponse = (accumulator: GoalAccumulator): GetYardGoalsResponse => ({
  goals: [
    ...Array.from(accumulator.byId.values(), ({ row }) => row),
    ...accumulator.malformedRows,
  ] as GoalEnvelope[],
});

const recordQueryMetrics = (metrics: QueryMetrics): void => {
  try {
    rumService.customEvent({
      type: RUM_EVENT_TYPES.PERFORMANCE_METRIC,
      message: "ryte_get_yard_goals_query",
      data: metrics,
    });
  } catch {
    // Telemetry cannot affect query outcome.
  }
};

const recordCompletion = (context: MetricContext, details: CompletionDetails): void => {
  const accumulator = context.accumulator;
  recordQueryMetrics({
    mode: context.mode,
    selectedConcurrency: context.selectedConcurrency,
    rangeDurationMs: context.rangeDurationMs,
    chunkCount: context.chunkCount,
    successfulChunkCount: context.successfulChunkCount,
    rawRowCount: details.rawRowCount ?? accumulator?.rawRowCount ?? 0,
    uniqueRowCount: details.uniqueRowCount ?? accumulator?.byId.size ?? 0,
    duplicateCount: details.duplicateCount ?? accumulator?.duplicateCount ?? 0,
    malformedRowCount: details.malformedRowCount ?? accumulator?.malformedRows.length ?? 0,
    durationMs: queryDuration(context.startedAt),
    maxChunkDurationMs: context.maxChunkDurationMs,
    splitCount: context.splitCount,
    maxSplitDepth: context.maxSplitDepth,
    ...(details.failedChunkOrdinal !== undefined
      ? { failedChunkOrdinal: details.failedChunkOrdinal }
      : {}),
    ...(details.error ? { errorStatus: details.error.status } : {}),
    terminationReason: details.terminationReason,
  });
};

const wideCounts = (
  data: unknown
): {
  rawRowCount: number;
  uniqueRowCount: number;
  duplicateCount: number;
  malformedRowCount: number;
} => {
  if (!isRecord(data) || !Array.isArray(data.goals)) {
    return { rawRowCount: 0, uniqueRowCount: 0, duplicateCount: 0, malformedRowCount: 0 };
  }
  const ids = new Set<string>();
  let duplicateCount = 0;
  let malformedRowCount = 0;
  for (const row of data.goals) {
    const { id } = readGoalIdentity(row);
    if (!id) {
      malformedRowCount += 1;
    } else if (ids.has(id)) {
      duplicateCount += 1;
    } else {
      ids.add(id);
    }
  }
  return {
    rawRowCount: data.goals.length,
    uniqueRowCount: ids.size,
    duplicateCount,
    malformedRowCount,
  };
};

const queryDuration = (startedAt: number): number => Math.max(0, Date.now() - startedAt);

const rangeDuration = (input: GetYardGoalsRequest): number =>
  Math.max(0, input.endEpochMs - input.startEpochMs);

export const isGetYardGoalsSupersededError = (error: unknown): boolean =>
  isRecord(error) &&
  error.status === "CUSTOM_ERROR" &&
  error.error === GET_YARD_GOALS_SUPERSEDED_ERROR;

const interruptedReason = (
  api: Pick<BaseQueryApi, "getState" | "signal">,
  initialEpoch: number
): "aborted" | "superseded" | undefined => {
  if (api.signal.aborted) return "aborted";
  if (readGoalReadEpoch(api) !== initialEpoch) return "superseded";
  return undefined;
};

const interruptedError = (reason: "aborted" | "superseded"): FetchBaseQueryError =>
  customError(reason === "aborted" ? FANOUT_ABORTED_ERROR : GET_YARD_GOALS_SUPERSEDED_ERROR);

interface RangeFailure {
  ok: false;
  error: FetchBaseQueryError;
  terminationReason: Exclude<TerminationReason, "success">;
  failedChunkOrdinal: number;
}

type RangeExecutionResult = { ok: true } | RangeFailure;
type RangeTransportResult = { ok: true; data: unknown; callOrdinal: number } | RangeFailure;

export const executeGetYardGoalsQuery = async (
  input: GetYardGoalsRequest,
  api: Pick<BaseQueryApi, "getState" | "signal">,
  baseQuery: GetYardGoalsBaseQuery
): Promise<QueryReturnValue<GetYardGoalsResponse, FetchBaseQueryError, object>> => {
  const startedAt = Date.now();
  const durationMs = rangeDuration(input);

  if (!isFanoutEnabled(api)) {
    const metrics: MetricContext = {
      mode: "wide",
      selectedConcurrency: 0,
      rangeDurationMs: durationMs,
      chunkCount: 1,
      startedAt,
      successfulChunkCount: 0,
      maxChunkDurationMs: 0,
      splitCount: 0,
      maxSplitDepth: 0,
    };
    try {
      const result = await baseQuery(
        buildTransportArgs(input, {
          startEpochMs: input.startEpochMs,
          endEpochMs: input.endEpochMs,
        })
      );
      const counts = "data" in result ? wideCounts(result.data) : wideCounts(undefined);
      metrics.successfulChunkCount = "data" in result ? 1 : 0;
      metrics.maxChunkDurationMs = queryDuration(startedAt);
      recordCompletion(metrics, {
        terminationReason: result.error ? "dependency_failure" : "success",
        error: result.error,
        failedChunkOrdinal: result.error ? 1 : undefined,
        ...counts,
      });
      return result as QueryReturnValue<GetYardGoalsResponse, FetchBaseQueryError, object>;
    } catch {
      const error = customError(FANOUT_UNEXPECTED_ERROR);
      metrics.maxChunkDurationMs = queryDuration(startedAt);
      recordCompletion(metrics, {
        terminationReason: "dependency_failure",
        error,
        failedChunkOrdinal: 1,
      });
      return { error };
    }
  }

  const ranges = buildFiveHourGoalRanges(input.startEpochMs, input.endEpochMs);
  const maxTransportCalls = ranges.length + MAX_ADAPTIVE_TRANSPORT_CALLS;
  const accumulator = createAccumulator();
  const metrics: MetricContext = {
    mode: "fanout",
    selectedConcurrency: INITIAL_CONCURRENCY,
    rangeDurationMs: durationMs,
    chunkCount: 0,
    startedAt,
    accumulator,
    successfulChunkCount: 0,
    maxChunkDurationMs: 0,
    splitCount: 0,
    maxSplitDepth: 0,
  };
  if (ranges.length === 0) {
    const error = customError(FANOUT_MALFORMED_ERROR);
    recordCompletion(metrics, {
      terminationReason: "malformed",
      error,
    });
    return { error };
  }

  const initialEpoch = readGoalReadEpoch(api);

  async function executeTransport(range: GoalTimeRange): Promise<RangeTransportResult> {
    const terminationReason = interruptedReason(api, initialEpoch);
    if (terminationReason) {
      return {
        ok: false,
        terminationReason,
        error: interruptedError(terminationReason),
        failedChunkOrdinal: metrics.chunkCount + 1,
      };
    }

    if (metrics.chunkCount >= maxTransportCalls) {
      return {
        ok: false,
        terminationReason: "dependency_failure",
        error: customError(FANOUT_SPLIT_LIMIT_ERROR),
        failedChunkOrdinal: metrics.chunkCount + 1,
      };
    }

    const chunkStartedAt = Date.now();
    metrics.chunkCount += 1;
    const callOrdinal = metrics.chunkCount;
    let result: QueryReturnValue<unknown, FetchBaseQueryError>;
    try {
      result = await baseQuery(buildTransportArgs(input, range, true));
    } catch {
      result = { error: customError(FANOUT_UNEXPECTED_ERROR) };
    }
    metrics.maxChunkDurationMs = Math.max(
      metrics.maxChunkDurationMs,
      queryDuration(chunkStartedAt)
    );

    const interruptionAfterCall = interruptedReason(api, initialEpoch);
    if (interruptionAfterCall) {
      return {
        ok: false,
        terminationReason: interruptionAfterCall,
        error: interruptedError(interruptionAfterCall),
        failedChunkOrdinal: callOrdinal,
      };
    }

    if (result.error) {
      return {
        ok: false,
        terminationReason: "dependency_failure",
        error: result.error,
        failedChunkOrdinal: callOrdinal,
      };
    }

    return { ok: true, data: result.data, callOrdinal };
  }

  async function consumeTransportResult(
    range: GoalTimeRange,
    splitDepth: number,
    result: RangeTransportResult
  ): Promise<RangeExecutionResult> {
    if (!result.ok) {
      const canSplit =
        result.error.status === 500 &&
        splitDepth < MAX_SPLIT_DEPTH &&
        range.startEpochMs < range.endEpochMs;
      if (!canSplit) return result;

      const midpoint = range.startEpochMs + Math.floor((range.endEpochMs - range.startEpochMs) / 2);
      metrics.splitCount += 1;
      metrics.maxSplitDepth = Math.max(metrics.maxSplitDepth, splitDepth + 1);

      const leftResult = await executeRange(
        { startEpochMs: range.startEpochMs, endEpochMs: midpoint },
        splitDepth + 1
      );
      if (!leftResult.ok) return leftResult;

      return executeRange(
        { startEpochMs: midpoint + 1, endEpochMs: range.endEpochMs },
        splitDepth + 1
      );
    }

    if (!accumulateResponse(accumulator, result.data)) {
      return {
        ok: false,
        terminationReason: "malformed",
        error: customError(FANOUT_MALFORMED_ERROR),
        failedChunkOrdinal: result.callOrdinal,
      };
    }
    metrics.successfulChunkCount += 1;
    return { ok: true };
  }

  async function executeRange(
    range: GoalTimeRange,
    splitDepth: number
  ): Promise<RangeExecutionResult> {
    const result = await executeTransport(range);
    return consumeTransportResult(range, splitDepth, result);
  }

  for (let batchStart = 0; batchStart < ranges.length; batchStart += INITIAL_CONCURRENCY) {
    const batchRanges = ranges.slice(batchStart, batchStart + INITIAL_CONCURRENCY);
    const batchResults = await Promise.all(batchRanges.map(executeTransport));

    for (let index = 0; index < batchRanges.length; index += 1) {
      const rangeResult = await consumeTransportResult(batchRanges[index], 0, batchResults[index]);
      if (!rangeResult.ok) {
        recordCompletion(metrics, {
          terminationReason: rangeResult.terminationReason,
          error: rangeResult.error,
          failedChunkOrdinal: rangeResult.failedChunkOrdinal,
        });
        return { error: rangeResult.error };
      }
    }
  }

  const data = accumulatedResponse(accumulator);
  recordCompletion(metrics, {
    terminationReason: "success",
  });
  return { data };
};
